// A deliberate Sign out while a meeting is recorded here (or held for
// "Carry on") asks first (recording decisions D9): stop and upload it
// first, or sign out anyway (the microphone stops; what was captured stays
// on this device and is sent the next time this account signs in, or, when
// this browser keeps no copy on the device, only while this page stays
// open: copy.signOutBody says which). An
// automatic sign-out (an expired session) never comes through here: it
// never stops the microphone (RecorderLifecycle keeps every guard).
//
//   const { signOut, dialog } = useSignOut();
//   <Button onClick={signOut}>Sign out</Button>{dialog}

import { useEffect, useState, type ReactNode } from "react";

import { Button } from "@/components/ui/Button";
import { Modal } from "@/components/ui/Modal";
import { useAuth } from "@/stores/auth";
import { signOutBody, waitingLine } from "./copy";
import { recorder } from "./recorderMedia";
import { signOutAsks, uploadSettled } from "./recorderModel";
import { useRecorder } from "./useRecorder";

export const SIGN_OUT_TITLE = "A meeting is being recorded";

export function useSignOut(): { signOut: () => void; dialog: ReactNode } {
  const logout = useAuth((s) => s.logout);
  const [open, setOpen] = useState(false);
  // Read when the button is pressed, not subscribed: the sidebar does not redraw with the level meter.
  const signOut = () => {
    if (signOutAsks(recorder().getSnapshot())) setOpen(true);
    else void logout();
  };
  const dialog = open ? <SignOutDialog onClose={() => setOpen(false)} logout={logout} /> : null;
  return { signOut, dialog };
}

function SignOutDialog({ onClose, logout }: { onClose: () => void; logout: () => Promise<void> }) {
  const r = useRecorder();
  const [step, setStep] = useState<"ask" | "uploading" | "leaving">("ask");

  // "Stop and upload it first": signed out once everything of this account is on the server.
  useEffect(() => {
    if (step === "uploading" && uploadSettled(r)) {
      setStep("leaving");
      void logout();
    }
  }, [step, r, logout]);

  const anyway = async () => {
    setStep("leaving");
    // The microphone stops (its last seconds are stored); held takes stay for the next sign-in.
    await r.stop();
    await logout();
  };
  const first = () => {
    setStep("uploading");
    void r.stopAndUpload();
  };

  const wait = waitingLine(r.waiting, r.storage, r.refusal);
  const progress = r.unsent > 0 ? `Uploading… ${r.unsent} ${r.unsent === 1 ? "part" : "parts"} to go.` : "Finishing the upload…";
  return (
    <Modal
      open
      onClose={step === "ask" ? onClose : () => undefined}
      dismissible={step === "ask"}
      title={SIGN_OUT_TITLE}
      description={step === "ask" ? signOutBody(r.storage) : undefined}
      footer={
        step === "ask" ? (
          // On a phone one button per row, the primary on top; never two and a lone third.
          <div className="flex w-full flex-col-reverse gap-2 sm:w-auto sm:flex-row sm:items-center">
            <Button variant="ghost" onClick={onClose}>Keep recording</Button>
            <Button variant="danger" onClick={() => void anyway()}>Sign out anyway</Button>
            <Button onClick={first}>Stop and upload first</Button>
          </div>
        ) : (
          <>
            <Button variant="ghost" onClick={onClose} disabled={step === "leaving"}>Stay signed in</Button>
            <Button variant="danger" onClick={() => void anyway()} loading={step === "leaving"}>Sign out anyway</Button>
          </>
        )
      }
    >
      {step !== "ask" && (
        <p className="text-sm text-fg-muted" role="status">
          {wait || progress} You are signed out once it has uploaded.
        </p>
      )}
      {/* Asking while the upload already waits (offline, storage full…): why, in the recorder's own words. */}
      {step === "ask" && wait && <p className="text-sm text-warning" role="status">{wait}</p>}
    </Modal>
  );
}
